/**
 * Utility functions for pet data normalization and extraction
 */

/**
 * Extract species name from species object or string
 * @param {string|object} species - Species as string or object with name property
 * @returns {string} Species name
 */
export const getSpeciesName = (species) => {
  if (!species) return null;
  if (typeof species === 'string') return species;
  if (species && typeof species === 'object' && species.name) return species.name;
  return null;
};

/**
 * Extract breed name from breed object or string
 * @param {string|object} breed - Breed as string or object with name property
 * @returns {string} Breed name
 */
export const getBreedName = (breed) => {
  if (!breed) return null;
  if (typeof breed === 'string') return breed;
  if (breed && typeof breed === 'object' && breed.name) return breed.name;
  return null;
};

/**
 * Get pet name from pet object (handles multilingual names)
 * @param {object} pet - Pet object
 * @returns {string} Pet name
 */
export const getPetName = (pet) => {
  if (!pet) return null;
  if (typeof pet.name === 'string') return pet.name;
  if (pet.name?.en) return pet.name.en;
  if (pet.name?.ar) return pet.name.ar;
  return null;
};

/**
 * Normalize pet info array - ensures it's always an array and extracts species/breed names
 * @param {array|object} petInfo - Pet info as single object, array, or nested in pets array
 * @returns {array} Normalized array of pet objects with extracted species/breed names
 */
export const normalizePetInfo = (petInfo) => {
  if (!petInfo) return [];
  
  // Handle different data structures
  let petInfoArray = petInfo;
  if (!Array.isArray(petInfo)) {
    petInfoArray = [petInfo];
  }
  
  // Normalize each pet object
  return petInfoArray.map(pet => {
    const species = getSpeciesName(pet.species);
    const breed = getBreedName(pet.breed);
    
    return {
      ...pet,
      species: species || pet.species,
      breed: breed || pet.breed
    };
  });
};
